import { BlockBaseCommand } from './BlockBaseCommand';
import type { BlockCommandDependencies } from './BlockBaseCommand';
import type { TableType } from '../grid/reduxStore/table.types';
import { AcknowledgmentResponseStatus, BlockEvents } from '../../../services/socket/SocketEvents';
import type { DefaultSocketResponseType } from '../../../services/socket/SocketEvents';
import { setSaveStatusBasedOnApiResponse } from '../grid/reduxStore/saveStatusSlice';
import { updateGridTableBlockState } from '../grid/reduxStore/editorSlice';

export interface TableContentCommandParams {
  blockId: string;
  sectionId: string;
  previousContent: TableType;
  newContent: TableType;
}

export class TableContentCommand extends BlockBaseCommand {
  readonly sectionId: string;
  private readonly blockId: string;
  private readonly previousContent: TableType;
  private readonly newContent: TableType;

  constructor(dependencies: BlockCommandDependencies, params: TableContentCommandParams) {
    super(dependencies);
    this.blockId = params.blockId;
    this.sectionId = params.sectionId;
    this.previousContent = structuredClone(params.previousContent);
    this.newContent = structuredClone(params.newContent);
  }

  getCurrentBlockId(): string {
    return this.blockId;
  }

  applyOptimistic(): void {
    this.setSaving();
    this.dispatch(
      updateGridTableBlockState({
        sectionId: this.sectionId,
        blockId: this.blockId,
        contentTable: this.newContent,
      })
    );
  }

  async persistToSocket(): Promise<boolean> {
    try {
      const response = await this.saveTableContentViaSocket(this.newContent);
      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackTo(this.previousContent);
        return false;
      }
      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackTo(this.previousContent);
        return false;
      }
      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));
      return true;
    } catch (error) {
      console.error('TableContentCommand persistToSocket failed:', error);
      this.rollbackTo(this.previousContent);
      return false;
    }
  }

  async undo(): Promise<boolean> {
    return this.applyAndPersist(this.previousContent, this.newContent);
  }

  async redo(): Promise<boolean> {
    return this.applyAndPersist(this.newContent, this.previousContent);
  }

  private async applyAndPersist(content: TableType, rollbackContent: TableType): Promise<boolean> {
    try {
      this.setSaving();
      this.dispatch(
        updateGridTableBlockState({
          sectionId: this.sectionId,
          blockId: this.blockId,
          contentTable: content,
        })
      );
      const response = await this.saveTableContentViaSocket(content);
      if (this.checkDocumentLockStatus(response.errorCode)) {
        this.rollbackTo(rollbackContent);
        return false;
      }
      if (response.status !== AcknowledgmentResponseStatus.OK) {
        this.rollbackTo(rollbackContent);
        return false;
      }
      this.dispatch(setSaveStatusBasedOnApiResponse({ status: response.status, errorCode: response.errorCode }));
      return true;
    } catch (error) {
      console.error('TableContentCommand applyAndPersist failed:', error);
      this.rollbackTo(rollbackContent);
      return false;
    }
  }

  private rollbackTo(content: TableType): void {
    this.dispatch(
      updateGridTableBlockState({
        sectionId: this.sectionId,
        blockId: this.blockId,
        contentTable: content,
      })
    );
    this.setNotSaved();
  }

  private buildTableContentPayload(content: TableType) {
    return {
      blockId: this.blockId,
      rows: content.rows,
      columns: content.columns,
      metadata: content.metadata,
      totalRows: content.totalRows,
    };
  }

  private saveTableContentViaSocket(content: TableType): Promise<DefaultSocketResponseType> {
    return new Promise<DefaultSocketResponseType>((resolve) => {
      const data = this.buildTableContentPayload(content);
      this.socketClient.publish(BlockEvents.BLOCK_TABLE_CONTENT_CHANGED, data, (response: DefaultSocketResponseType) => {
        resolve(response);
      });
    });
  }

  toJSON(): Record<string, any> {
    return {
      blockId: this.blockId,
      sectionId: this.sectionId,
      previousContent: this.previousContent,
      newContent: this.newContent,
    };
  }
}
